import React, { useState } from 'react';
import { ISnackbarInput } from '../../Components/CustomUIElements/StyledSnackbar';
import { ICandidateSourcingPool } from '../types';
import { sharedClasses } from '../../Components/CustomUIElements/sharedClasses';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import Api from '../API';
import EditIcon from '@mui/icons-material/Edit';
import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import { Box, IconButton, TextField } from '@mui/material';

export default function PoolName({
  selectedPool,
  setSnackbarState,
  refetch
}: {
  selectedPool: ICandidateSourcingPool;
  setSnackbarState: (snackbarState: ISnackbarInput) => void;
  refetch: () => void;
}) {
  const [poolName, setPoolName] = useState<string>(selectedPool?.title);
  const [isEditingName, setIsEditingName] = useState(false);
  const [editedName, setEditedName] = useState<string>(selectedPool?.title);

  const queryClient = useQueryClient();

  const { mutate: updatedCandidatePool, isLoading: isLoadingUpdatePool } = useMutation({
    mutationFn: (data: string) => Api.updateCandidatePool(selectedPool.id, { title: data }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['candidatePools'] });
      refetch();
    },
    onError() {
      setSnackbarState({
        message: `There was an error updating the candidate pool, please refresh the page and try again.`,
        state: 'error'
      });
    }
  });

  const handleEditClick = () => {
    setIsEditingName(true);
    setEditedName(poolName);
  };

  const handleSaveName = () => {
    if (editedName.trim() && editedName !== poolName) {
      updatedCandidatePool(editedName.trim());
      setPoolName(editedName.trim());
    }
    setIsEditingName(false);
  };

  const handleCancelEdit = () => {
    setIsEditingName(false);
    setEditedName(poolName);
  };

  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, height: '50px' }}>
      {isEditingName ? (
        <>
          <TextField
            required
            label="Candidate pool name"
            value={editedName}
            onChange={(e) => setEditedName(e.target.value)}
            size="small"
            sx={{ minWidth: '290px' }}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                handleSaveName();
              } else if (e.key === 'Escape') {
                handleCancelEdit();
              }
            }}
            autoFocus
          />
          <IconButton
            onClick={handleSaveName}
            size="small"
            sx={{ color: 'green' }}
            disabled={!editedName.trim()}
          >
            <CheckIcon />
          </IconButton>
          <IconButton onClick={handleCancelEdit} size="small" sx={{ color: 'red' }}>
            <CloseIcon />
          </IconButton>
        </>
      ) : (
        <>
          <Box sx={sharedClasses.sectionHeader}>{poolName}</Box>
          <IconButton onClick={handleEditClick} size="small" sx={{ ml: 1 }}>
            <EditIcon fontSize="small" />
          </IconButton>
        </>
      )}
    </Box>
  );
}
